import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { PermissionAccessLevel } from '@/api/generated/schemas/permissionAccessLevel';

const LEVELS = [
  PermissionAccessLevel.None,
  PermissionAccessLevel.Read,
  PermissionAccessLevel.Edit,
] as const;

const LEVEL_LABEL_KEY: Record<PermissionAccessLevel, string> = {
  None: 'level.none',
  Read: 'level.read',
  Edit: 'level.edit',
};

const ACTIVE_CLASS: Record<PermissionAccessLevel, string> = {
  None: 'bg-muted text-foreground border-input',
  Read: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-800',
  Edit: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-200 dark:border-green-800',
};

interface PermissionLevelControlProps {
  /** Explicitní grant uzlu (`None` = bez grantu). */
  value: PermissionAccessLevel;
  disabled?: boolean;
  onChange: (level: PermissionAccessLevel) => void;
}

/** Tri-state přepínač úrovně grantu (Žádné / Čtení / Editace) — ukládá se hned při změně. */
export function PermissionLevelControl({ value, disabled, onChange }: PermissionLevelControlProps) {
  const { t } = useTranslation('security');

  return (
    <div className="border-input inline-flex shrink-0 rounded-md border" role="group">
      {LEVELS.map((level, i) => {
        const isActive = value === level;
        return (
          <button
            key={level}
            type="button"
            disabled={disabled || isActive}
            aria-pressed={isActive}
            className={cn(
              'px-2 py-0.5 text-xs transition-colors',
              i > 0 && 'border-input border-l',
              i === 0 && 'rounded-l-md',
              i === LEVELS.length - 1 && 'rounded-r-md',
              isActive
                ? ACTIVE_CLASS[level]
                : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground',
              disabled && 'cursor-not-allowed opacity-60'
            )}
            onClick={() => onChange(level)}
          >
            {t(LEVEL_LABEL_KEY[level])}
          </button>
        );
      })}
    </div>
  );
}
